import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  LinearProgress,
  Grid,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import { SportsSoccer as ShieldIcon } from '@mui/icons-material';
import TeamLogo from '../team/TeamLogo';
import unifiedApi from '../../utils/unifiedApi';
import { useAuth } from '../../contexts/AuthContext';

const GoldenGloveKeeperCard = ({ keeper, maxCleanSheets, actualPosition, showPosition }) => {
  const percentage = maxCleanSheets > 0 ? (keeper.cleanSheets / maxCleanSheets) * 100 : 0;
  const isTopKeeper = actualPosition === 1;

  return (
    <Card 
      elevation={isTopKeeper ? 4 : 2}
      sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        border: isTopKeeper ? '2px solid #FFD700' : 'none',
        position: 'relative',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: isTopKeeper ? '0 8px 20px rgba(255,215,0,0.3)' : '0 6px 16px rgba(0,0,0,0.1)',
        },
      }}
    >
      {isTopKeeper && (
        <Box
          sx={{
            position: 'absolute',
            top: -8,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1
          }}
        >
          <Chip
            icon={<ShieldIcon />}
            label="Golden Glove"
            size="small"
            sx={{
              backgroundColor: '#FFD700',
              color: '#000',
              fontWeight: 'bold',
              fontSize: '0.7rem'
            }}
          />
        </Box>
      )}
      
      <CardContent sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Team Logo - Large Size */}
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            mb: 2,
            minHeight: 80,
            position: 'relative'
          }}
        >
          {/* Position Badge - Only show if showPosition is true */}
          {showPosition && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                backgroundColor: isTopKeeper ? '#FFD700' : 'primary.main',
                color: isTopKeeper ? '#000' : 'white',
                borderRadius: '50%',
                width: 28,
                height: 28,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.9rem',
                fontWeight: 'bold',
                zIndex: 1
              }}
            >
              {actualPosition}
            </Box>
          )}
          
          {/* Team Logo or Spare Badge */}
          {keeper.isSpare ? (
            <Box
              sx={{
                width: 80,
                height: 80,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'warning.main',
                borderRadius: '50%',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '0.9rem'
              }}
            >
              SPARE
            </Box>
          ) : (
            <TeamLogo 
              team={{ logoUrl: keeper.logoUrl, name: keeper.teamName }}
              size={80}
              sx={{ flexShrink: 0 }}
            />
          )}
        </Box>

        {/* Player Name */}
        <Typography 
          variant="h6" 
          sx={{ 
            fontSize: '1rem',
            fontWeight: 'bold',
            lineHeight: 1.2,
            mb: 1,
            textAlign: 'center'
          }}
        >
          {keeper.fullName}
        </Typography>

        {/* Team Name */}
        <Typography 
          variant="body2" 
          color={keeper.isSpare ? "warning.main" : "text.secondary"}
          sx={{ 
            fontSize: '0.8rem',
            textAlign: 'center',
            mb: 2,
            fontWeight: keeper.isSpare ? 'bold' : 'normal'
          }}
        >
          {keeper.teamName}
        </Typography>

        {/* Clean Sheets Section */}
        <Box sx={{ mt: 'auto' }}>
          {/* Clean Sheet Count */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
            <ShieldIcon color="primary" sx={{ mr: 0.5, fontSize: '1.2rem' }} />
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 'bold',
                color: isTopKeeper ? '#FFD700' : 'primary.main'
              }}
            >
              {keeper.cleanSheets}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
              clean sheet{keeper.cleanSheets !== 1 ? 's' : ''}
            </Typography>
          </Box>

          {/* Progress Bar */}
          <Box>
            <LinearProgress
              variant="determinate"
              value={percentage}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: 'rgba(0,0,0,0.1)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: isTopKeeper ? '#FFD700' : 'primary.main',
                  borderRadius: 4,
                },
              }}
            />
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                textAlign: 'center', 
                mt: 0.5,
                fontSize: '0.7rem'
              }}
            >              
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const GoldenGlove = ({ 
  leagueSlug = null, 
  leagueId = null, 
  limit = 5, 
  minimumCleanSheets = 0,
  title = "Golden Glove",
  isPublic = true 
}) => {
  const [keepers, setKeepers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();
  const _mode = user ? user.role : 'public';

  useEffect(() => {
    const loadCleanSheetLeaders = async () => {
      try {
        setLoading(true);
        setError(null);

        let data;
        if (isPublic && leagueSlug) {
          // Use public API with league slug
          data = await unifiedApi.getCleanSheets(leagueSlug, { limit, minimumCleanSheets });
        } else if (!isPublic && leagueId) {
          // TODO: There's no leagueId-based clean sheets endpoint; callers must pass leagueSlug
          // (GET /api/leagues/{key}/clean-sheets).
          throw new Error('Authenticated API not yet implemented');
        } else {
          throw new Error('Either leagueSlug (for public) or leagueId (for authenticated) must be provided');
        }

        if (!data) {
          setError('No clean sheet data available for this league');
          return;
        }

        if (!data.keepers || data.keepers.length === 0) {
          setKeepers([]);
          return;
        }

        setKeepers(data.keepers);
      } catch (err) {
        const errorMessage = err.message || 'Failed to load clean sheet leaders';
        setError(errorMessage);
        console.error('Error loading clean sheet leaders:', err);
      } finally {
        setLoading(false);
      }
    };

    if ((isPublic && leagueSlug) || (!isPublic && leagueId)) {
      loadCleanSheetLeaders();
    } else {
      setLoading(false);
      setError('Missing required parameters');
    }
  }, [leagueSlug, leagueId, limit, minimumCleanSheets, isPublic]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="info" sx={{ my: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!keepers || keepers.length === 0) {
    return (
      <Alert severity="info" sx={{ my: 2 }}>
        No clean sheet records found for this league yet.
      </Alert>
    );
  }

  const maxCleanSheets = keepers.length > 0 ? keepers[0].cleanSheets : 1;

  // Calculate positions accounting for ties
  const keepersWithPositions = [];
  let currentPosition = 1;
  
  for (let i = 0; i < keepers.length; i++) {
    const keeper = keepers[i];
    
    // If this isn't the first keeper and they have fewer clean sheets than the previous,
    // update the position to be the current index + 1
    if (i > 0 && keeper.cleanSheets < keepers[i - 1].cleanSheets) {
      currentPosition = i + 1;
    }
    
    // Check if this is the first occurrence of this clean sheet count
    const isFirstWithThisScore = i === 0 || keepers[i - 1].cleanSheets !== keeper.cleanSheets;
    
    keepersWithPositions.push({
      ...keeper,
      actualPosition: currentPosition,
      showPosition: isFirstWithThisScore
    });
  }

  return (
    <Box>
      {/* Section Header */}
      <Box sx={{ mb: 3 }}>
        <Typography 
          variant="h5" 
          component="h2" 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            fontWeight: 'bold',
            mb: 1
          }}
        >
          <ShieldIcon color="primary" />
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Top {keepers.length} clean sheet keeper{keepers.length !== 1 ? 's' : ''} in the league
        </Typography>
      </Box>

      {/* Keepers Grid - Max 5 per row */}
      <Grid container spacing={2}>
        {keepersWithPositions.map((keeper) => (
          <Grid 
            size={{ xs: 12, sm: 6, md: 4, lg: 2.4, xl: 2.4 }}
            key={`${keeper.userTenantId}-${keeper.teamId}`}
          >
            <GoldenGloveKeeperCard 
              keeper={keeper} 
              maxCleanSheets={maxCleanSheets} 
              actualPosition={keeper.actualPosition}
              showPosition={keeper.showPosition}
            />
          </Grid>
        ))}
      </Grid>

      {/* Show message if fewer than requested limit */}
      {keepers.length < limit && keepers.length > 0 && (
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ textAlign: 'center', mt: 2 }}
        >
          Showing all {keepers.length} keeper{keepers.length !== 1 ? 's' : ''} with clean sheets in the league
        </Typography>
      )}
    </Box>
  );
};

export default GoldenGlove;